"use client";

import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, ChevronDown, ChevronRight, MessageSquare, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { MemoizedMarkdown } from "@west-tennessee-consulting/meridian/components/MemoizedMarkdown";
import type { TaskSummaryData } from "@/lib/types/ai";

export interface TaskAISummaryContentProps {
  displayedContent: string;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  summary: TaskSummaryData | null;
  showMetadata: boolean;
  className?: string;
}

/**
 * Content display component for task AI summary
 * Shows markdown content with collapsible teaser and metadata
 */
export function TaskAISummaryContent({
  displayedContent,
  isExpanded,
  onToggleExpanded,
  summary,
  showMetadata,
  className,
}: TaskAISummaryContentProps) {
  // ONE markdown renderer (`MemoizedMarkdown`), not a per-surface component map.
  // The map that used to live here had no `table`/`th`/`td`/`hr`/`a`/`pre`
  // renderer and leaned on `prose` for those — but Hub does not load
  // @tailwindcss/typography, so `.prose` defines variables nothing reads and
  // Tailwind preflight had already stripped the browser defaults. A table in a
  // task summary therefore rendered as run-together text.
  const markdownComponent = useMemo(() => {
    if (!displayedContent) return null;

    return (
      <div className="max-w-none text-sm">
        <MemoizedMarkdown id="task-ai-summary" content={displayedContent} />
      </div>
    );
  }, [displayedContent]);

  return (
    <div className={cn("space-y-3", className)}>
      {/* Teaser Bar */}
      <div
        className="flex items-center justify-between p-3 bg-card-50 dark:bg-card-800 rounded-lg cursor-pointer hover:bg-card-100 dark:hover:bg-card-700 transition-colors"
        onClick={onToggleExpanded}
      >
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <span className="text-sm font-medium">AI Summary Ready</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Click to expand</span>
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Full Content */}
      {isExpanded && (
        <div className="space-y-3">
          {markdownComponent}

          {showMetadata && summary?.metadata && (
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              <Badge variant="secondary" className="text-xs">
                <MessageSquare className="h-3 w-3 mr-1" />
                {summary.metadata.noteCount} notes analyzed
              </Badge>
              <Badge variant="secondary" className="text-xs">
                <Clock className="h-3 w-3 mr-1" />
                {summary.metadata.processingTime}ms
              </Badge>
              <Badge variant="secondary" className="text-xs">
                <CheckCircle className="h-3 w-3 mr-1" />
                {new Date(summary.createdAt).toLocaleString()}
              </Badge>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
